import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

type InstallPrompt = Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const [installPrompt, setInstallPrompt] = useState<InstallPrompt | null>(null);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const beforeInstall = (event: Event) => { event.preventDefault(); setInstallPrompt(event as InstallPrompt); };
    const onlineHandler = () => setOnline(true);
    const offlineHandler = () => setOnline(false);
    window.addEventListener('beforeinstallprompt', beforeInstall);
    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstall);
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
    };
  }, []);

  const install = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <Link className="brand" to="/"><span className="brand-mark">?</span><span>Quem Me Conhece?</span></Link>
        <nav className="nav-links">
          <NavLink to="/criar">Criar</NavLink>
          <NavLink to="/creditos">Créditos</NavLink>
          {user && <NavLink to={`/perfil/${user.publicSlug}`}>Meu perfil</NavLink>}
        </nav>
        <div className="topbar-actions">
          {installPrompt && <button className="button button-install" onClick={install}>⬇ Instalar</button>}
          {user ? (
            <>
              <Link className="credit-pill" to="/creditos">🪙 {user.unlimitedCredits ? '∞' : user.credits}</Link>
              <button className="button button-ghost small" onClick={() => void logout()}>Sair</button>
            </>
          ) : <Link className="button button-secondary small" to="/entrar">Entrar</Link>}
        </div>
      </header>
      {!online && <div className="offline-banner">Você está offline. Pagamentos e novas respostas voltarão quando a conexão retornar.</div>}
      <main>{children}</main>
      <footer className="footer">Quem Me Conhece? · PWA social com recompensas e pagamentos protegidos.</footer>
    </div>
  );
}
